import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import LegalEditor from "@/components/editor/LegalEditor";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

interface PublicDocData {
    document_id: string;
    title: string;
    content_text: string;
    version_number: number;
    access_level: "view" | "comment" | "edit";
    client_name: string;
}

export const PublicSharedDocument: React.FC = () => {
    const { shareToken } = useParams<{ shareToken: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [docData, setDocData] = useState<PublicDocData | null>(null);

    useEffect(() => {
        if (!shareToken) {
            setError("Enlace de documento no válido");
            setLoading(false);
            return;
        }

        const fetchDoc = async () => {
            try {
                const res = await fetch(`${API}/api/v1/documents/public-share/${shareToken}`);
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.detail || "No se pudo cargar el documento compartido");
                }
                const data = await res.json();
                setDocData(data);
            } catch (err: any) {
                setError(err.message || "Error al cargar el documento");
            } finally {
                setLoading(false);
            }
        };

        fetchDoc();
    }, [shareToken]);

    const handleClientSave = async (content: string) => {
        const canEdit = docData?.access_level === "edit" || (docData?.access_level as any) === "full_edit";
        if (!shareToken || !docData || !canEdit) return;
        try {
            const res = await fetch(`${API}/api/v1/documents/public-share/${shareToken}/versions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    content_text: content,
                    client_name: docData.client_name || "Cliente",
                    comment: `Edición guardada por el cliente (${docData.client_name || "Invitado"})`,
                }),
            });
            if (res.ok) {
                toast.success("✅ Cambios guardados correctamente");
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(`Error al guardar: ${err.detail || "Error en el servidor"}`);
            }
        } catch (err) {
            console.error("Save client version error:", err);
            toast.error("Error al conectar con el servidor.");
        }
    };

    if (loading) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-slate-50">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    <p className="text-sm font-medium text-slate-600">Cargando documento compartido…</p>
                </div>
            </div>
        );
    }

    if (error || !docData) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-slate-50 p-4">
                <div className="max-w-md text-center bg-white p-8 rounded-xl shadow-sm border border-slate-200">
                    <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-slate-800 mb-2">Enlace no disponible</h2>
                    <p className="text-sm text-slate-600 mb-6">{error || "El enlace ha expirado o es incorrecto."}</p>
                    <a href="/" className="inline-block px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 transition">
                        Volver al Inicio
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen w-full flex flex-col bg-slate-100 overflow-hidden">
            {(() => {
                const canEdit = docData.access_level === "edit" || (docData.access_level as any) === "full_edit";
                return (
                    <LegalEditor
                        documentId={docData.document_id}
                        documentTitle={docData.title}
                        initialContent={docData.content_text}
                        userName={docData.client_name ? `${docData.client_name}` : "Cliente"}
                        isPublicShare={true}
                        shareToken={shareToken}
                        accessLevel={canEdit ? "edit" : (docData.access_level as any) === "comment_only" ? "comment" : "view"}
                        onSave={canEdit ? handleClientSave : undefined}
                    />
                );
            })()}
        </div>
    );
};
export default PublicSharedDocument;
